import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { AlertColor } from '@mui/lab';
import {
  Button,
  CircularProgress,
  IconButton,
  Theme,
  Tooltip,
} from '@mui/material';
import { green } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import { GridRenderCellParams } from '@mui/x-data-grid';
import clsx from 'clsx';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';

import {
  DataTable,
  IColDef,
} from 'src/app/modules/Manage/pages/queues-table/DataTable';

import { useBusinessPermissions } from '../../common/helperFunctions/useBusinessPermissions';
import {
  loadableEnum,
  useMakePHPRequest,
} from '../../common/useMakePHPRequest';
import {
  trackAddMySMSTemplate,
  trackEditSMSTemplate,
} from '../../common/userTracking';
import { Cadence } from '../../lib/kixie-entities/tables/Cadence';
import { agentUserId } from '../../recoil/atoms/agentUserId';
import { IUserAtom, user } from '../../recoil/atoms/user';
import { getSmsTemplateByAgent } from '../../recoil/selectors/getSmsTemplateByAgent';
import {
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../services/rest/getApiMutation';
import { endpointEnum } from '../../services/rest/makeAPIRequest';
import { AddSmsTemplateModal } from '../components/modals/AddSmsTemplateModal';
import { DeleteSmsTemplateModal } from '../components/modals/DeleteSmsTemplateModal';
import { UpdateSmsTemplateModal } from '../components/modals/UpdateSmsTemplateModal';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  EnhancedTableToolbar,
  ToolbarAddDeleteButton,
} from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';
import { DashboardTourExtraSpotlights } from '../pages/dashboardTour/DashboardTourExtraSpotlights';

const useStyles = makeStyles((theme: Theme) => ({
  dropdown: {
    marginLeft: '1vw',
    marginTop: '10px',
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -22,
    marginLeft: -22,
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
  actionButton: {
    whiteSpace: 'nowrap',
    flexDirection: 'column',
    display: 'flex',
    alignItems: 'center',
    '& article': {
      margin: 0,
    },
    '& p': {
      lineHeight: 'normal',
      whiteSpace: 'normal',
      textTransform: 'uppercase',
    },
  },
}));
export function SmsTemplates(props: { agentId: string }) {
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const classes = useStyles();
  // state being passed to update Modal
  const [defaultName, setDefaultName] = useState('');
  const [updateId, setUpdateId] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  // state for showing add new sms template modal
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  // state for showing delete sms template modal
  const [showA, setShowA] = useState(false);
  const handleCloseA = () => setShowA(false);
  const handleShowA = () => setShowA(true);
  // state for showing update modal
  const [showB, setShowB] = useState(false);
  const handleCloseB = () => setShowB(false);
  const handleShowB = () => setShowB(true);
  const currentUserBeingViewed = useRecoilValueLoadable(agentUserId);
  const [userLoadable, setUserAtom] = useRecoilStateLoadable(user);
  const userContents = userLoadable.contents as IUserAtom;
  const [deleteGroupId, setDeleteGroupId] = useState<string | undefined>('');
  const [deleteStatus, setDeleteStatus] = useState(loadableEnum.EMPTY);
  const makeRequest = useMakePHPRequest({
    userAtom: userContents,
    setStatus: setDeleteStatus,
  });
  const mutation = useMutation(getApiMutation(userContents, setUserAtom));
  const setRefetchToggleForSmsTemplates = useSetRecoilState(
    refetchToggle(endpointEnum.GET_SMS_TEMPLATE)
  );
  // replace with memo and cached data and uuidv4 id with smsTemplateid
  const templates = useRecoilValueLoadable<any>(getSmsTemplateByAgent);
  const [selected, setSelected] = useState<any>([]);
  const templatesCached = useRef([]);
  templatesCached.current = useMemo(
    () =>
      templates.contents?.filteredByAgent
        ? templates.contents?.filteredByAgent?.map((o: any) => ({
            ...o,
            id: o.smsTemplateid,
          }))
        : templatesCached.current,
    [templates]
  );
  function changeAction(row: any) {
    setDefaultName(row.smsTemplateName);
    setUpdateId(row.smsTemplateid);
    setTemplateContent(row.smsTemplateMessage);
    // setVmId(row.voicemaildropid);
    handleShowB();
  }
  const userAtom = useRecoilValueLoadable(user);
  const loginUser = userAtom.contents?.user ?? {};
  function actionsFormatter(params: GridRenderCellParams) {
    // create Actions element here
    return deleteGroupId === params.row.smsTemplateid ? (
      <div className={classes.actionButton}>
        <article>
          <Tooltip title="Confirm Deletion">
            <IconButton
              aria-label="accept"
              onClick={async (event) => {
                event.preventDefault(); // stop link
                // insert action to delete here
                // await makeRequest({
                //   methodName: methodNameEnum.DELETE_SMS_TEMPLATE,
                //   payload: {
                //   smstid: params.row.smsTemplateid,
                //   userid: currentUserBeingViewed.contents.id,
                // },
                // });
                const data = {
                  call: methodNameEnum.DELETE_SMS_TEMPLATE,
                  apikey: userContents.apikey,
                  businessid: userContents.businessid,
                  smstid: params.row.smsTemplateid,
                  userid: props.agentId,
                };
                const response = await mutation.mutateAsync({
                  endpoint: endpointEnum.DELETE_SMS_TEMPLATE,
                  payload: data,
                });
                // insert refetch toggle here
                setRefetchToggleForSmsTemplates({});
                if (response?.data?.success) {
                  enqueueSnackbarWithStyles({
                    message: `Successfully Deleted ${params.row.smsTemplateName} SMS Template`,
                    alertProps: { severity: 'success' },
                  });
                } else {
                  let message:
                    | string
                    | React.ReactNode = `Encountered an error trying to delete ${params.row.smsTemplateName} SMS Template`;
                  let severity: AlertColor = 'error';

                  const errorOnValidate = response?.data?.errorOnValidate;

                  if (
                    errorOnValidate.errorType ===
                      'FK_CADENCE_ACTION__SMS_TEMPLATES' &&
                    errorOnValidate.entities
                  ) {
                    const cadences = errorOnValidate.entities;

                    message = (
                      <div>
                        Cannot delete SMS template since this is being used in
                        the following cadences:
                        <ul>
                          {cadences.map((cadence: Cadence) => (
                            <li key={cadence.cadenceId}>
                              <a
                                href={`/manage/cadences/${cadence.cadenceId}/edit`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: 'white',
                                  textDecoration: 'underline',
                                }}
                              >
                                {cadence.cadenceName}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                    severity = 'warning';
                  }

                  enqueueSnackbarWithStyles({
                    message,
                    alertProps: { severity },
                  });
                }
              }}
              disabled={
                mutation.isLoading || deleteStatus === loadableEnum.LOADING
              }
              size="large"
            >
              <CheckIcon
                color={
                  mutation.isLoading || deleteStatus === loadableEnum.LOADING
                    ? 'disabled'
                    : 'primary'
                }
              />
              {mutation.isLoading ||
                (deleteStatus === loadableEnum.LOADING && (
                  <CircularProgress
                    size={43}
                    className={classes.buttonProgress}
                  />
                ))}
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel">
            <IconButton
              aria-label="cancel"
              onClick={(event) => {
                event.preventDefault(); // stop link
                // reset groupid hide confirm / cancel deletion
                setDeleteGroupId(undefined);
              }}
              disabled={
                mutation.isLoading || deleteStatus === loadableEnum.LOADING
              }
              size="large"
            >
              <ClearIcon
                color="disabled"
                className={clsx({
                  [classes.deleteIconButton]:
                    !mutation.isLoading &&
                    deleteStatus !== loadableEnum.LOADING, // || deleteCancelTokenSource,
                })}
              />
            </IconButton>
          </Tooltip>
        </article>
      </div>
    ) : (
      <div>
        <Tooltip title="Edit">
          <IconButton
            name="edit"
            onClick={() => {
              trackEditSMSTemplate();
              changeAction(params.row);
            }}
            size="large"
          >
            <CreateIcon color="primary" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            aria-label="delete"
            onClick={() => setDeleteGroupId(params.row.smsTemplateid)}
            size="large"
          >
            <DeleteIcon className={classes.deleteIconButton} />
          </IconButton>
        </Tooltip>
      </div>
    );
  }
  const readOnly = useBusinessPermissions().accountReadOnly;
  const columns = [
    {
      field: 'smsTemplateName',
      headerName: 'Name',
      width: 200,
    },
    {
      field: 'smsTemplateMessage',
      headerName: 'Message',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'voicemaildropid',
      headerName: 'Actions',
      headerAlign: 'center',
      width: 120,
      hide: readOnly,
      enableSorting: false,
      renderCell: actionsFormatter,
    },
  ] /* satisfies */ as IColDef[];
  // console.log(templates, 'templates');
  const ToolbarComponent = useCallback(
    (props) => (
      <EnhancedTableToolbar
        {...props}
        dataType="SMS Templates"
        numSelected={selected.length}
        rightComponent={
          <ToolbarAddDeleteButton
            numSelected={selected.length}
            onClickForAdd={() => {
              if (
                window.location.pathname ===
                `/my-profile/${loginUser.userid}/sms-template-help`
              ) {
                trackAddMySMSTemplate();
              }
              handleShow();
            }}
            onClickForDelete={handleShowA}
            isLoading={props.isLoading}
          />
        }
        centerComponent={
          <Button
            variant="outlined"
            color="primary"
            endIcon={<HelpOutlineIcon />}
            target="_blank"
            href="https://support.kixie.com/hc/en-us/articles/360013080574-How-Do-SMS-Message-Templates-Work-"
            rel="noopener noreferrer"
          >
            How to Use SMS Templates
          </Button>
        }
      />
    ),
    [handleShow, handleShowA, selected.length]
  );

  return (
    <>
      <DashboardTourExtraSpotlights />
      <DataTable
        enableSelectAll={true}
        setSelected={setSelected}
        rows={templatesCached?.current ?? []}
        columns={columns}
        loading={templates.state === 'loading'}
        sortModel={[
          {
            field: 'smsTemplateName',
            sort: 'asc',
          },
        ]}
        ToolbarComponent={ToolbarComponent}
        selected={selected}
      />

      <AddSmsTemplateModal
        handleClose={handleClose}
        show={show}
        setRefetchToggleForSmsTemplates={setRefetchToggleForSmsTemplates}
        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
        currentUserBeingViewed={props.agentId}
      />
      <DeleteSmsTemplateModal
        handleClose={handleCloseA}
        handleShow={handleShowA}
        show={showA}
        selected={selected}
        setSelected={setSelected}
        userAtom={userContents}
        setUserAtom={setUserAtom}
        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
        makeRequest={makeRequest}
        currentUserBeingViewed={currentUserBeingViewed}
        setRefetchToggleForSmsTemplates={setRefetchToggleForSmsTemplates}
      />
      <UpdateSmsTemplateModal
        templateContent={templateContent}
        templateName={defaultName}
        updateId={updateId}
        handleClose={handleCloseB}
        handleShow={handleShowB}
        show={showB}
        setRefetchToggleForSmsTemplates={setRefetchToggleForSmsTemplates}
        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
        makeRequest={makeRequest}
        methodNameEnum={methodNameEnum}
        currentUserBeingViewed={currentUserBeingViewed}
      />
    </>
  );
}
